/* eslint-disable max-lines-per-function */
import _ from 'lodash';
import axiosBase from 'axios';
import axios from 'src/workspace/axios';
import { isValueEmpty } from 'src/workspace/utils';
import { FormFieldState } from '../types';
import { getSignedUrlPayload } from './queries';
import { FormSliceState } from './types';

const getMultimediaPayload = (
  multimediaInput: FormFieldState,
  bucket: string,
) =>
  typeof multimediaInput.value === 'string'
    ? multimediaInput.value
    : JSON.stringify({
        items: multimediaInput.value,
        bucket: bucket || multimediaInput.value[0]?.bucket || '',
        media_type:
          multimediaInput.value[0]?.type ||
          multimediaInput.value[0]?.mime ||
          '',
      });

const getUpdatedMultimediaInput = (
  multimediaInput: FormFieldState,
  beforeSaveHookConfig: any,
  bucket = '',
) => {
  const { valueFormat } = beforeSaveHookConfig.multimedia;
  const { code, value } = multimediaInput;

  if (isValueEmpty(value)) {
    return { code, value: valueFormat === 'url' ? [] : '' };
  }

  if (valueFormat === 'url') {
    return {
      code,
      value: Array.isArray(value) ? value.map((file) => file.uri) : value,
    };
  }

  return { code, value: getMultimediaPayload(multimediaInput, bucket) };
};

const uploadFilesToS3 = async (
  multimediaInputs: FormFieldState[],
  beforeSaveHookConfig: any,
) => {
  const { payload, isSignedUrlRequired } =
    getSignedUrlPayload(multimediaInputs);

  if (isSignedUrlRequired) {
    const response = await axios.post(
      beforeSaveHookConfig?.multimedia?.uploadUrl ||
        '/api/generate_signed_urls',
      payload,
    );

    const { multimedia_response: multimediaResponse, bucket } =
      response?.data?.result || {};

    const promises: any = [];
    const updatedInputs = (multimediaInputs || []).map((multimediaInput) => {
      if (multimediaInput.code in multimediaResponse) {
        const uploadedFilesValue = multimediaResponse[multimediaInput.code].map(
          (file) => ({
            ...file,
            file: multimediaInput.value.find((doc) => doc.id === file.id)?.file,
            label: file.filename,
            bucket,
            uri: file.pre_signed_url,
          }),
        );

        uploadedFilesValue.forEach((file) =>
          promises.push(
            axios.put(file.uri, file.file, {
              headers: {
                'Content-Type': file.mime,
              },
              withCredentials: false,
            }),
          ),
        );
        multimediaInput.value = _.unionBy(
          uploadedFilesValue,
          multimediaInput.value,
          'id',
        );
      }
      return getUpdatedMultimediaInput(
        multimediaInput,
        beforeSaveHookConfig,
        bucket,
      );
    });

    try {
      const result = await Promise.all(promises);
      // eslint-disable-next-line no-console
      console.log(result);
    } catch (error) {
      Promise.reject(error);
    }

    return updatedInputs;
  }
  return multimediaInputs.map((multimediaInput) =>
    getUpdatedMultimediaInput(multimediaInput, beforeSaveHookConfig),
  );
};

const uploadSingleRevyreDocument = async (file: any, uploadUrl: string) => {
  if (!file?.file) {
    return file;
  }

  const response = await axios.post(uploadUrl, {
    docType: 'OTHER',
    fileName: file?.fileName || file?.label || file?.file?.name,
    contentType: file?.file?.type || file?.mime,
    fileSize: file?.file?.size || file?.size,
  });

  const { mutatedEntity } = response?.data?.payload || {};
  const { uploadUrl: presignedUrl, id: documentId } = mutatedEntity || {};

  if (!presignedUrl) {
    throw new Error('Missing Revyre DMS upload URL');
  }

  await axiosBase.put(presignedUrl, file.file, {
    headers: {
      'Content-Type': file.mime || file.file?.type,
      'x-ms-blob-type': 'BlockBlob',
    },
    withCredentials: false,
  });

  return {
    ...file,
    id: documentId || file.id,
    uri: presignedUrl,
    presigned_url: presignedUrl,
  };
};

const buildRevyreUploadedInputs = async (
  multimediaInputs: FormFieldState[],
  beforeSaveHookConfig: any,
) => {
  const updatedInputs = (multimediaInputs || []).map((multimediaInput) => {
    const inputValue = Array.isArray(multimediaInput.value)
      ? multimediaInput.value
      : [];

    const uploadedFilesValue = inputValue.map((file) => {
      if (!file?.file) {
        return file;
      }

      return uploadSingleRevyreDocument(
        file,
        beforeSaveHookConfig?.multimedia?.uploadUrl,
      );
    });

    return {
      code: multimediaInput.code,
      value: uploadedFilesValue,
    };
  });

  try {
    const resolvedInputs = await Promise.all(
      updatedInputs.map(async ({ code, value }) => ({
        code,
        value: await Promise.all(value),
      })),
    );

    return resolvedInputs.map(({ code, value }) => {
      if (isValueEmpty(value)) {
        return {
          code,
          value:
            beforeSaveHookConfig.multimedia.valueFormat === 'url' ? [] : '',
        };
      }

      return {
        code,
        value:
          beforeSaveHookConfig.multimedia.valueFormat === 'documentId'
            ? value?.map((file) => file.id)
            : JSON.stringify({
                items: (value ?? []).map(({ file, ...rest }) => rest),
                media_type: value?.[0]?.type || value?.[0]?.mime || '',
              }),
      };
    });
  } catch (error) {
    return Promise.reject(error);
  }
};

export const beforeSaveHook = async (
  configData: FormSliceState['config']['data'],
  formSliceState: FormSliceState,
): Promise<Record<string, any>> => {
  const { formData, config } = formSliceState;
  const { fields } = formData;
  const {
    beforeSaveHookConfig = {
      multimedia: {
        isMultimediaBeforeSaveHookDisabled: false,
        uploadUrl: '/api/getS3signedUrl',
        valueFormat: 'url',
      },
    },
  } = config;
  const beforeHookData = {};

  if (!beforeSaveHookConfig?.multimedia?.isMultimediaBeforeSaveHookDisabled) {
    const multimediaInputs = configData
      .filter(
        (inputField) =>
          inputField.type === 'multimedia' &&
          !_.isNil(fields[inputField?.code]),
      )
      .map((inputField) => ({ ...inputField, ...fields[inputField?.code] }));
    const updatedInputs = config?.metaData?.revyre
      ? await buildRevyreUploadedInputs(multimediaInputs, beforeSaveHookConfig)
      : await uploadFilesToS3(multimediaInputs, beforeSaveHookConfig);

    updatedInputs.forEach(({ code, value }) => {
      beforeHookData[code] = value;
    });
  }
  return beforeHookData;
};
